import { BrandChip } from '@/components/BrandChip';
import { BrandSearchList } from '@/components/BrandSearchList';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useAppliesToGuard } from '@/db/useAppliesToGuard';
import { useCurrentUserId } from '@/db/useCurrentUserId';
import { allBrandsState, sharedNameplatesState, sharedNameplateTableState, spacesState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import logger from '@/util/logger';
import { planSharedNameplate, sharedNameplateEntries } from '@rock/brands';
import type { HqlCatalog } from '@rock/hql';
import type {
    Brand,
    BrandId,
    BrandRequestId,
    SharedNameplate,
    SharedNameplateRoute,
    SpaceId,
    Timestamp,
} from '@rock/types';
import { normalizeBrandName } from '@rock/util';
import type { FC } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
    applySharedNameplate,
    deleteSharedNameplate,
    previewSharedNameplate,
    type SharedNameplatePreview,
} from './sharedNameplateActions';

const log = logger('SharedNameplateDialog');
const UNROUTED = '__unrouted__';
const LISTED_KEYS_CAP = 10;

/**
 * "Two companies print this name." One dialog, four parts: the bare name and why it is shared; the
 * qualified brands (sides); what the name means per space (a side, or unrouted with a note — the
 * default, because a wrong brand is worse than none); and a preview of the deny keys and what they
 * replace. A name the table already holds opens in edit mode. Apply runs the applies-to guard (an
 * approved query naming the bare key would stop compiling), then writes the row, its deny placeholders,
 * strips the bare name from the sides' aliases, settles the brand request it came from, and republishes
 * the index.
 */
export const SharedNameplateDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    /** The bare name to start from (a brand page's name, a request's name); editable until applied. */
    initialName?: string;
    /** The request this was opened from, resolved as `shared-nameplate` on apply. */
    fromRequestId?: BrandRequestId;
    onApplied?: (nameplate: SharedNameplate) => void;
    onDeleted?: () => void;
}> = ({ open, onOpenChange, initialName = '', fromRequestId, onApplied, onDeleted }) => {
    const by = useCurrentUserId();
    const guard = useAppliesToGuard();
    const brands = useAsyncAtomValue(allBrandsState, []);
    const spaces = useAsyncAtomValue(spacesState, []);
    const nameplates = useAsyncAtomValue(sharedNameplatesState, []);
    const table = useAsyncAtomValue(sharedNameplateTableState, []);
    const brandsById = useMemo(() => new Map(brands.map((brand) => [brand.id, brand])), [brands]);

    const [name, setName] = useState(initialName);
    const [reason, setReason] = useState('');
    const [sideIds, setSideIds] = useState<BrandId[]>([]);
    const [routes, setRoutes] = useState<SharedNameplateRoute[]>([]);
    const [pickingSide, setPickingSide] = useState(false);
    const [preview, setPreview] = useState<SharedNameplatePreview>();
    const [busy, setBusy] = useState(false);
    const [loadedKey, setLoadedKey] = useState<string>();

    const key = normalizeBrandName(name).key;
    // A name the table already holds is edited, never recreated over: the stored row seeds the form.
    const existing = useMemo(() => nameplates.find((nameplate) => nameplate.key === key), [nameplates, key]);

    useEffect(() => {
        if (!open) return;
        setName(initialName);
        setReason('');
        setSideIds([]);
        setRoutes([]);
        setPickingSide(false);
        setPreview(undefined);
        setLoadedKey(undefined);
    }, [open, initialName]);

    useEffect(() => {
        if (!existing || loadedKey === existing.key) return;
        setReason(existing.reason);
        setSideIds(existing.sides);
        setRoutes(existing.routes);
        setLoadedKey(existing.key);
    }, [existing, loadedKey]);

    const sides = useMemo(
        () => sideIds.map((id) => brandsById.get(id)).filter((brand): brand is Brand => !!brand),
        [sideIds, brandsById],
    );
    // Routes only ever point at a side; dropping a side drops its routes too.
    const liveRoutes = useMemo(
        () => routes.filter((route) => !route.brandId || sideIds.includes(route.brandId)),
        [routes, sideIds],
    );
    const planned = useMemo(() => {
        if (!key || sides.length < 2) return undefined;
        const now = new Date().toISOString() as Timestamp;
        return planSharedNameplate(
            { name, reason, sides: sideIds, routes: liveRoutes, now, existing },
            table.filter((entry) => entry.key !== existing?.key),
        );
    }, [key, name, reason, sideIds, liveRoutes, existing, table, sides.length]);

    // Keyed on the bare key and the sides: the preview reads alias docs, and neither the reason nor a
    // route note changes what it shows.
    useEffect(() => {
        if (!key || sides.length < 2) {
            setPreview(undefined);
            return;
        }
        let cancelled = false;
        previewSharedNameplate(key, sides, brands)
            .then((result) => !cancelled && setPreview(result))
            .catch((err) => log.error('Shared nameplate preview failed', { err, key }));
        return () => {
            cancelled = true;
        };
    }, [key, sides, brands]);

    const routeFor = (spaceId: SpaceId) => liveRoutes.find((route) => route.spaceId === spaceId);
    const setRoute = (spaceId: SpaceId, next: Partial<SharedNameplateRoute>) => {
        setRoutes((current) => {
            const others = current.filter((route) => route.spaceId !== spaceId);
            const { brandId, note } = {
                ...(current.find((route) => route.spaceId === spaceId) ?? { spaceId }),
                ...next,
            };
            // An unrouted space with nothing to say is the default and needs no entry.
            if (!brandId && !note) return others;
            return [...others, { spaceId, ...(brandId ? { brandId } : {}), ...(note ? { note } : {}) }];
        });
    };

    const handleApply = async () => {
        if (!by || !planned || 'refusal' in planned) return;
        setBusy(true);
        try {
            const entry = sharedNameplateEntries([planned.plan.nameplate])[0]!;
            await guard(
                `Marking ${key} shared`,
                (catalog): HqlCatalog => ({
                    ...catalog,
                    sharedNameplates: [...catalog.sharedNameplates.filter((item) => item.key !== key), entry],
                }),
            );
            const outcome = await applySharedNameplate(
                { name, reason, sides: sideIds, routes: liveRoutes, existing },
                table,
                sides,
                brands,
                by,
                fromRequestId,
            );
            if (outcome.status === 'refused') {
                toast.error('Not applied', { description: outcome.reason });
                return;
            }
            toast.success(`${outcome.nameplate.key} is a shared nameplate`, {
                description: `${sides.length} sides, ${liveRoutes.filter((route) => route.brandId).length} routed space(s). Index republishing.`,
            });
            onOpenChange(false);
            onApplied?.(outcome.nameplate);
        } catch (err) {
            log.error('Failed to apply shared nameplate', { err, key });
            toast.error('Unable to apply the shared nameplate', {
                description: err instanceof Error ? err.message : undefined,
            });
        } finally {
            setBusy(false);
        }
    };

    const handleDelete = async () => {
        if (!existing) return;
        setBusy(true);
        try {
            await guard(
                `Un-sharing ${existing.key}`,
                (catalog): HqlCatalog => ({
                    ...catalog,
                    sharedNameplates: catalog.sharedNameplates.filter((item) => item.key !== existing.key),
                }),
            );
            const keptDenials = await deleteSharedNameplate(existing);
            if (keptDenials.length > 0) {
                toast.warning(`${existing.key} is no longer a shared nameplate`, {
                    description: `Deny docs not stamped with it were left in place: ${listKeys(keptDenials)}`,
                });
            } else {
                toast.success(`${existing.key} is no longer a shared nameplate`);
            }
            onOpenChange(false);
            onDeleted?.();
        } catch (err) {
            log.error('Failed to delete shared nameplate', { err, key: existing.key });
            toast.error('Unable to un-share the nameplate', {
                description: err instanceof Error ? err.message : undefined,
            });
        } finally {
            setBusy(false);
        }
    };

    const refusal = planned && 'refusal' in planned ? planned.refusal : undefined;
    const blocked = preview?.bareBrand;

    return (
        <Dialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>
                        {existing ? `Shared nameplate ${existing.key}` : 'Mark as shared nameplate'}
                    </DialogTitle>
                    <DialogDescription>
                        The bare name stops resolving to any one brand; its qualified sides are what equipment links to,
                        by space where the table says that is safe.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    <section className="space-y-2">
                        <Input
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            placeholder="The shared name (e.g. Maytag)"
                            disabled={busy}
                            autoComplete="off"
                        />
                        {key && (
                            <div className="section-description">
                                Key: <span className="font-mono">{key}</span>
                                {existing ? ' — already shared; editing it.' : ''}
                            </div>
                        )}
                        <Textarea
                            value={reason}
                            onChange={(event) => setReason(event.target.value)}
                            placeholder="Why it is shared — which company sells what (stored on the deny docs)."
                            disabled={busy}
                        />
                    </section>

                    <section className="space-y-2">
                        <div className="text-sm font-medium">Sides — the qualified brands, one per company</div>
                        <div className="flex flex-wrap gap-2">
                            {sides.map((side) => (
                                <BrandChip
                                    key={side.id}
                                    brand={side}
                                    onRemove={() => setSideIds((ids) => ids.filter((id) => id !== side.id))}
                                />
                            ))}
                            <Button variant="outline" size="sm" onClick={() => setPickingSide(true)} disabled={busy}>
                                Add side…
                            </Button>
                        </div>
                        {pickingSide && (
                            <BrandSearchList
                                brands={brands}
                                initialQuery={normalizeBrandName(name).display}
                                exclude={Object.fromEntries(sideIds.map((id) => [id, 'Side']))}
                                disabled={busy}
                                emptyMessage="No qualified brand yet — add it from the Brands page first (e.g. “Maytag Appliances”)."
                                onSelect={(brand) => {
                                    setSideIds((ids) => [...ids, brand.id]);
                                    setPickingSide(false);
                                }}
                            />
                        )}
                    </section>

                    <section className="space-y-2">
                        <div className="text-sm font-medium">Routes — what the bare name means per space</div>
                        <div className="space-y-2">
                            {spaces.map((space) => {
                                const route = routeFor(space.id);
                                return (
                                    <div key={space.id} className="grid grid-cols-[140px_200px_1fr] items-center gap-2">
                                        <span className="text-sm">{space.name}</span>
                                        <Select
                                            value={route?.brandId ?? UNROUTED}
                                            onValueChange={(value) =>
                                                setRoute(space.id, {
                                                    brandId: value === UNROUTED ? undefined : (value as BrandId),
                                                })
                                            }
                                            disabled={busy}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={UNROUTED}>Unrouted</SelectItem>
                                                {sides.map((side) => (
                                                    <SelectItem key={side.id} value={side.id}>
                                                        {side.canonicalName}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Input
                                            value={route?.note ?? ''}
                                            onChange={(event) =>
                                                setRoute(space.id, { note: event.target.value || undefined })
                                            }
                                            placeholder={route?.brandId ? 'Note (optional)' : 'Why unrouted (optional)'}
                                            disabled={busy}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    <section className="space-y-1 rounded-md border bg-muted/40 p-3 text-sm">
                        {refusal ? (
                            <div className="text-destructive">{refusal}</div>
                        ) : !preview ? (
                            <div className="text-muted-foreground">
                                Add the name, a reason and two sides to preview.
                            </div>
                        ) : (
                            <>
                                <div>{preview.denyKeys.length} deny key(s) reserved, OCR variants included.</div>
                                {preview.replaces.length > 0 && (
                                    <div>
                                        Stops resolving:{' '}
                                        {preview.replaces
                                            .map(
                                                (entry) =>
                                                    `${entry.key} → ${entry.brand?.canonicalName ?? '(missing brand)'}`,
                                            )
                                            .join(', ')}
                                    </div>
                                )}
                                {preview.sidesListingBareName.length > 0 && (
                                    <div>
                                        The bare name is stripped from the aliases of{' '}
                                        {preview.sidesListingBareName.map((side) => side.canonicalName).join(', ')}.
                                    </div>
                                )}
                                {blocked && (
                                    <div className="text-destructive">
                                        {blocked.canonicalName} is keyed on the bare name. Triage its models (each
                                        belongs to one company), merge it into the right side, then mark the nameplate
                                        shared.
                                    </div>
                                )}
                                <div className="text-muted-foreground">
                                    Applying checks every approved query first: one that names the bare key would stop
                                    compiling, and the change is refused until it is qualified.
                                </div>
                            </>
                        )}
                    </section>
                </div>

                <DialogFooter>
                    {existing && (
                        <Button variant="destructive" onClick={handleDelete} disabled={busy || !by}>
                            Un-share
                        </Button>
                    )}
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                        Cancel
                    </Button>
                    <Button onClick={handleApply} disabled={busy || !by || !preview || !!blocked || !!refusal}>
                        {busy ? 'Working…' : existing ? 'Save' : 'Mark shared'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

function listKeys(keys: string[]): string {
    const listed = keys.slice(0, LISTED_KEYS_CAP).join(', ');
    return keys.length > LISTED_KEYS_CAP ? `${listed} and ${keys.length - LISTED_KEYS_CAP} more` : listed;
}
